import React, { useCallback, useMemo, useState, useContext } from 'react';
import { useParams } from 'react-router';
import { StaticContext } from '@sm/webassets';
import { t } from '@sm/intl';
import { Box } from '@wds/box';
import { Accordion } from '@wds/accordion';
import { IconCaretUp, IconCaretDown } from '@wds/icons';
import getFeatureCategories from '~pricing/transformers/details';
import FeatureTooltip from '~pricing/components/Tooltip';
import teamGroupings from '~pricing/transformers/details/teamGroupings';
import individualGroupings from '~pricing/transformers/details/individualGroupings';
import { PackageDataSet } from '~shared/types/pricing/package';
import { NavRouteParams } from '~pricing/components/Navigation/types';
import { ToggleState } from './types';

import FeatureCell from './FeatureCell';
import useCateogryStyles from './useStyles';
import COPY from '~app/pages/Pricing/copy';

const CaretDown = <IconCaretDown title="Down Arrow" />;
const CaretUp = <IconCaretUp title="Up Arrow" />;

export default function Categories({
  displayedPackages,
  isMobile,
}: {
  displayedPackages: PackageDataSet;
  isMobile?: boolean;
}): JSX.Element {
  const {
    environment: { slLanguageLocale },
  } = useContext(StaticContext);
  const { canonicalPath } = useParams<NavRouteParams>();

  const { AccordionControl, DetailRow, Feature, TooltipContainer, TooltipIndicator, Tooltip, ExpandCollapse } =
    useCateogryStyles({
      packageCount: displayedPackages.length,
    });
  const expandAll = t(COPY.EXPAND_ALL);
  const collapseAll = t(COPY.COLLAPSE_ALL);

  const [toggleData, setToggleData] = useState<ToggleState>({
    initialLoad: isMobile,
    isExpanded: !isMobile ?? true,
    text: isMobile ? expandAll : collapseAll,
    icon: isMobile ? CaretUp : CaretDown,
  });

  const categories = useMemo(() => {
    const groupings = canonicalPath === 'teams' ? teamGroupings : individualGroupings;
    return getFeatureCategories(displayedPackages, groupings, slLanguageLocale);
  }, [displayedPackages, canonicalPath, slLanguageLocale]);

  const handleToggle = useCallback(
    (event: React.SyntheticEvent): void => {
      event.preventDefault();
      setToggleData({
        initialLoad: false,
        isExpanded: !toggleData.isExpanded,
        text: toggleData.isExpanded ? expandAll : collapseAll,
        icon: toggleData.isExpanded ? CaretUp : CaretDown,
      });
    },
    [toggleData.isExpanded, expandAll, collapseAll]
  );

  const key = isMobile ? 'Mobile' : 'Desktop';

  return (
    <>
      <button type="button" className={ExpandCollapse} onClick={handleToggle}>
        {toggleData.text}
        {toggleData.icon}
      </button>
      <Box className={AccordionControl} data-testid="Categories">
        <Accordion multiple>
          {categories.map((category, index) => (
            <Accordion.Item
              expanded={toggleData.isExpanded || (toggleData.initialLoad && index === 0)}
              title={category.name}
              key={`${key}-${category.name}`}
            >
              <section role="table" aria-label={category.name}>
                {category?.features?.map(feature => (
                  <div className={DetailRow} key={`${key}-${feature.name}`} role="row" tabIndex={-1}>
                    <div className={Feature} role="rowheader" aria-label={feature.summary} tabIndex={-1}>
                      {feature.summary}
                      {feature.tooltip && feature.tooltipHeading && (
                        <span className={TooltipContainer}>
                          <span className={TooltipIndicator}>?</span>
                          <span className={Tooltip}>
                            <FeatureTooltip tooltip={feature.tooltip} tooltipHeading={feature.tooltipHeading} />
                          </span>
                        </span>
                      )}
                    </div>
                    {feature?.packages.map(pkg => (
                      <FeatureCell {...pkg} key={`${key}-${category.name}-${pkg.packageName}`} />
                    ))}
                  </div>
                ))}
              </section>
            </Accordion.Item>
          ))}
        </Accordion>
      </Box>
    </>
  );
}
